/**
 * InspectionMapsBlock — Adresseingabe A→B, OSRM-Routenberechnung,
 * eingebettete Google-Maps-Anzeige.
 *
 * Verwendet das Backend ``POST /api/maps/route`` (Nominatim + OSRM,
 * beides kostenlos, keine API-Keys). Die Werte (km, Fahrzeit, Embed-URL)
 * werden in der Akte gespeichert; ``onDistanceCalculated`` propagiert
 * km automatisch ins Honorar.
 */
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Navigation, Loader2, MapPin, Route, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function InspectionMapsBlock({
  gutachterAddress,
  customerAddress,
  onGutachterAddressChange,
  onCustomerAddressChange,
  onDistanceCalculated,
}) {
  const [fromAddr, setFromAddr] = useState(gutachterAddress || "");
  const [toAddr, setToAddr] = useState(customerAddress || "");
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState(null);  // { km, duration_min, embed_url, from_coords, to_coords }

  React.useEffect(() => { setFromAddr(gutachterAddress || ""); }, [gutachterAddress]);
  React.useEffect(() => { setToAddr(customerAddress || ""); }, [customerAddress]);

  const handleCalc = async () => {
    if (!fromAddr.trim() || !toAddr.trim()) {
      toast.error("Bitte beide Adressen eingeben.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/maps/route", {
        from_address: fromAddr,
        to_address: toAddr,
      });
      setRoute(data);
      onGutachterAddressChange?.(fromAddr);
      onCustomerAddressChange?.(toAddr);
      onDistanceCalculated?.(data.km, data.duration_min);
      toast.success(`Route berechnet: ${data.km} km · ${data.duration_min} min`);
    } catch (e) {
      toast.error(apiError(e, "Routenberechnung fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-cyan-200/60 bg-white shadow-sm overflow-hidden" data-testid="inspection-maps-block">
      <div className="px-4 py-3 border-b border-zinc-100 bg-gradient-to-r from-cyan-50/40 to-white flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-lg brand-gradient-soft flex items-center justify-center">
          <Navigation className="h-4 w-4 text-cyan-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-display font-semibold text-zinc-900">Anfahrts-Route</div>
          <div className="text-[11px] text-zinc-500">Adresse Gutachter → Kunde — wir berechnen die Auto-Distanz</div>
        </div>
        {route && (
          <div className="flex items-center gap-3 text-xs text-zinc-600">
            <span className="inline-flex items-center gap-1 font-semibold text-cyan-700">
              <Route className="h-3.5 w-3.5" /> {route.km} km
            </span>
            <span className="inline-flex items-center gap-1 font-semibold text-zinc-600">
              <Clock className="h-3.5 w-3.5" /> {route.duration_min} min
            </span>
          </div>
        )}
      </div>

      <div className="p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 flex items-center gap-1.5">
            <span className="inline-flex h-4 w-4 rounded-full bg-emerald-500 text-white items-center justify-center text-[10px] font-bold">A</span>
            Gutachter
          </Label>
          <Input
            className="mt-1.5"
            value={fromAddr}
            onChange={(e) => setFromAddr(e.target.value)}
            placeholder="Büro-Adresse mit PLZ"
            data-testid="maps-from-addr"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500 flex items-center gap-1.5">
            <span className="inline-flex h-4 w-4 rounded-full bg-rose-500 text-white items-center justify-center text-[10px] font-bold">B</span>
            Kunde / Besichtigungsort
          </Label>
          <Input
            className="mt-1.5"
            value={toAddr}
            onChange={(e) => setToAddr(e.target.value)}
            placeholder="Kunden-Adresse mit PLZ"
            data-testid="maps-to-addr"
          />
        </div>
        <Button
          type="button"
          onClick={handleCalc}
          disabled={loading || !fromAddr.trim() || !toAddr.trim()}
          className="brand-gradient-bg text-white h-9 px-4 text-xs"
          data-testid="maps-calc-btn"
        >
          {loading
            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : <Route className="h-3.5 w-3.5 mr-1.5" />}
          Route berechnen
        </Button>
      </div>

      {route?.embed_url && (
        <div className="border-t border-zinc-100">
          <iframe
            src={route.embed_url}
            title="Route Gutachter → Kunde"
            className="w-full block"
            style={{ height: "320px", border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="maps-embed-iframe"
          />
          <div className="px-4 py-2 text-[11px] text-zinc-500 bg-zinc-50 border-t border-zinc-100">
            <MapPin className="h-3 w-3 inline mr-1" />
            <strong>{route.from_coords?.label?.split(",")[0] || "Start"}</strong>
            {" → "}
            <strong>{route.to_coords?.label?.split(",")[0] || "Ziel"}</strong>
          </div>
        </div>
      )}
    </div>
  );
}
